import type { FieldError, Locale } from "../api/types";
import { chromeLocale } from "../lib/locale-context";

/** Sign-in, session and list strings (UX-01 §4.15, Revision 3.6). Thai is provisional until the BMS PM confirms it. */
export type AuthTextKey =
  | "loginIntro"
  | "loginEmail"
  | "loginPassword"
  | "loginSubmit"
  | "loginBusy"
  | "loginMissing"
  | "loginEmailMissing"
  | "loginPasswordMissing"
  | "loginIncorrect"
  | "loginLocked"
  | "loginRateLimited"
  | "expiredTitle"
  | "expiredBody"
  | "expiredSubmit"
  | "signOut"
  | "changeTitle"
  | "changeIntro"
  | "currentPassword"
  | "newPassword"
  | "newPasswordHint"
  | "confirmPassword"
  | "changeSubmit"
  | "changeSaving"
  | "changeErrors"
  | "fieldsMissing"
  | "currentMissing"
  | "newMissing"
  | "confirmMissing"
  | "confirmMismatch"
  | "changeDone"
  | "listRestarted"
  | "passkeyOr"
  | "passkeyButton"
  | "passkeyBusy"
  | "passkeyNotSetUp"
  | "passkeyUnavailable";

export const AUTH_TEXT: Record<Locale, Record<AuthTextKey, string>> = {
  en: {
    // Partners sign in too, so the line names the Giraffe account, not a TFG account.
    loginIntro: "Use the work email and password of your Giraffe account.",
    loginEmail: "Work email",
    loginPassword: "Password",
    loginSubmit: "Sign in",
    loginBusy: "Signing in…",
    loginMissing: "Enter your work email and your password.",
    loginEmailMissing: "Enter your work email.",
    loginPasswordMissing: "Enter your password.",
    loginIncorrect: "Email or password is incorrect.",
    loginLocked: "This account is locked after too many failed sign-in attempts. Try again in about 15 minutes or ask a system admin.",
    loginRateLimited: "Too many sign-in attempts. Wait a minute, then try again.",
    expiredTitle: "Your session expired",
    expiredBody: "Sign in again to continue. What you typed on this page is kept.",
    expiredSubmit: "Sign in and continue",
    signOut: "Sign out",
    changeTitle: "Change your password",
    changeIntro: "You need a new password before you continue.",
    currentPassword: "Current password",
    newPassword: "New password",
    newPasswordHint: "At least 12 characters, with a number, a lowercase letter and a capital letter, and at least 4 different characters.",
    confirmPassword: "Repeat the new password",
    changeSubmit: "Change password",
    changeSaving: "Changing password…",
    changeErrors: "Fix these to continue",
    fieldsMissing: "Fill in all three password fields.",
    currentMissing: "Enter your current password.",
    newMissing: "Enter a new password.",
    confirmMissing: "Type the new password again.",
    confirmMismatch: "The new passwords do not match. Type the same new password twice.",
    changeDone: "Password changed. You were signed out on your other devices.",
    listRestarted: "The list changed while you were viewing it, so it restarted from the newest item.",
    // Batch 18 GRF-46 (design B18-PASSKEY-AND-RISK §1.12).
    passkeyOr: "or",
    passkeyButton: "Sign in with a passkey",
    passkeyBusy: "Waiting for your passkey…",
    passkeyNotSetUp: "This passkey isn't set up for Giraffe here. Sign in with your password, then add it from your profile.",
    passkeyUnavailable: "Passkey sign-in is switched off. Sign in with your password.",
  },
  th: {
    loginIntro: "ใช้อีเมลที่ทำงานและรหัสผ่านของบัญชี Giraffe",
    loginEmail: "อีเมลที่ทำงาน",
    loginPassword: "รหัสผ่าน",
    loginSubmit: "เข้าสู่ระบบ",
    loginBusy: "กำลังเข้าสู่ระบบ…",
    loginMissing: "กรอกอีเมลที่ทำงานและรหัสผ่าน",
    loginEmailMissing: "กรอกอีเมลที่ทำงาน",
    loginPasswordMissing: "กรอกรหัสผ่าน",
    loginIncorrect: "อีเมลหรือรหัสผ่านไม่ถูกต้อง",
    loginLocked: "บัญชีนี้ถูกล็อกเพราะเข้าสู่ระบบไม่สำเร็จหลายครั้ง ลองใหม่ในอีกประมาณ 15 นาที หรือติดต่อผู้ดูแลระบบ",
    loginRateLimited: "พยายามเข้าสู่ระบบบ่อยเกินไป รอประมาณหนึ่งนาทีแล้วลองใหม่",
    expiredTitle: "เซสชันหมดอายุ",
    expiredBody: "เข้าสู่ระบบอีกครั้งเพื่อทำงานต่อ ข้อความที่พิมพ์ไว้ในหน้านี้ยังอยู่",
    expiredSubmit: "เข้าสู่ระบบและทำงานต่อ",
    signOut: "ออกจากระบบ",
    changeTitle: "เปลี่ยนรหัสผ่าน",
    changeIntro: "ต้องตั้งรหัสผ่านใหม่ก่อนใช้งานต่อ",
    currentPassword: "รหัสผ่านปัจจุบัน",
    newPassword: "รหัสผ่านใหม่",
    newPasswordHint: "อย่างน้อย 12 ตัวอักษร มีตัวเลข ตัวพิมพ์เล็กและตัวพิมพ์ใหญ่ภาษาอังกฤษ และมีตัวอักษรที่ไม่ซ้ำกันอย่างน้อย 4 ตัว",
    confirmPassword: "กรอกรหัสผ่านใหม่อีกครั้ง",
    changeSubmit: "เปลี่ยนรหัสผ่าน",
    changeSaving: "กำลังเปลี่ยนรหัสผ่าน…",
    changeErrors: "แก้ไขรายการต่อไปนี้ก่อนดำเนินการต่อ",
    fieldsMissing: "กรอกรหัสผ่านให้ครบทั้งสามช่อง",
    currentMissing: "กรอกรหัสผ่านปัจจุบัน",
    newMissing: "กรอกรหัสผ่านใหม่",
    confirmMissing: "กรอกรหัสผ่านใหม่อีกครั้ง",
    confirmMismatch: "รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน กรอกให้ตรงกัน",
    changeDone: "เปลี่ยนรหัสผ่านแล้ว และออกจากระบบในอุปกรณ์อื่นแล้ว",
    listRestarted: "รายการมีการเปลี่ยนแปลงระหว่างที่ดูอยู่ จึงเริ่มแสดงใหม่ตั้งแต่รายการล่าสุด",
    // Batch 18 GRF-46: provisional, unconfirmed by a Thai reviewer.
    passkeyOr: "หรือ",
    passkeyButton: "เข้าสู่ระบบด้วยพาสคีย์",
    passkeyBusy: "กำลังรอพาสคีย์ของคุณ…",
    passkeyNotSetUp: "พาสคีย์นี้ยังไม่ได้ตั้งค่าไว้สำหรับ Giraffe ที่นี่ เข้าสู่ระบบด้วยรหัสผ่าน แล้วเพิ่มพาสคีย์จากหน้าโปรไฟล์",
    passkeyUnavailable: "ปิดการเข้าสู่ระบบด้วยพาสคีย์อยู่ เข้าสู่ระบบด้วยรหัสผ่าน",
  },
};

/** Auth chrome is English-only regardless of the caller's locale (2026-09-17) — see `chromeLocale`. */
function lang(locale: Locale | undefined): Locale {
  return chromeLocale(locale);
}

export function authText(key: AuthTextKey, locale: Locale | undefined): string {
  return AUTH_TEXT[lang(locale)][key];
}

/** Change-password 422 errors by ASP.NET Identity error code (UX-01 §4.15, 11-C31). `{n}` comes from the code's parameter. */
export type PasswordErrorCode =
  | "PasswordMismatch"
  | "PasswordTooShort"
  | "PasswordRequiresDigit"
  | "PasswordRequiresLower"
  | "PasswordRequiresUpper"
  | "PasswordRequiresNonAlphanumeric"
  | "PasswordRequiresUniqueChars"
  | "PasswordSameAsCurrent";

export const PASSWORD_ERRORS: Record<Locale, Record<PasswordErrorCode, string>> = {
  en: {
    PasswordMismatch: "Current password is incorrect.",
    PasswordTooShort: "Use at least {n} characters.",
    PasswordRequiresDigit: "Include at least one number (0–9).",
    PasswordRequiresLower: "Include at least one lowercase letter (a–z).",
    PasswordRequiresUpper: "Include at least one capital letter (A–Z).",
    PasswordRequiresNonAlphanumeric: "Include at least one symbol, such as ! or #.",
    PasswordRequiresUniqueChars: "Use at least {n} different characters.",
    PasswordSameAsCurrent: "Choose a new password that is different from your current one.",
  },
  th: {
    PasswordMismatch: "รหัสผ่านปัจจุบันไม่ถูกต้อง",
    PasswordTooShort: "ใช้อย่างน้อย {n} ตัวอักษร",
    PasswordRequiresDigit: "ต้องมีตัวเลขอย่างน้อย 1 ตัว (0–9)",
    PasswordRequiresLower: "ต้องมีตัวอักษรภาษาอังกฤษพิมพ์เล็กอย่างน้อย 1 ตัว (a–z)",
    PasswordRequiresUpper: "ต้องมีตัวอักษรภาษาอังกฤษพิมพ์ใหญ่อย่างน้อย 1 ตัว (A–Z)",
    PasswordRequiresNonAlphanumeric: "ต้องมีสัญลักษณ์อย่างน้อย 1 ตัว เช่น ! หรือ #",
    PasswordRequiresUniqueChars: "ต้องมีตัวอักษรที่ไม่ซ้ำกันอย่างน้อย {n} ตัว",
    PasswordSameAsCurrent: "ตั้งรหัสผ่านใหม่ที่ไม่ซ้ำกับรหัสผ่านปัจจุบัน",
  },
};

/** A change-password field error; `params` (11-C31, not in the contract yet) carries the template's number when sent. */
export type PasswordFieldError = Pick<FieldError, "code" | "message"> & { params?: unknown };

export function isPasswordErrorCode(code: string): code is PasswordErrorCode {
  return Object.hasOwn(PASSWORD_ERRORS.en, code);
}

/**
 * The number a template needs. 11-C31 asks SA for `errors[].params`; until the contract names the key it is read
 * defensively (`n`, then Identity's option names), and failing that from the first number in the server's message.
 */
function parameterOf(error: PasswordFieldError): number | undefined {
  const { params } = error;
  if (typeof params === "object" && params !== null) {
    for (const key of ["n", "requiredLength", "requiredUniqueChars", "length", "min"]) {
      const value = (params as Record<string, unknown>)[key];
      if (typeof value === "number") return value;
    }
  }
  const inMessage = /\d+/.exec(error.message)?.[0];
  return inMessage === undefined ? undefined : Number(inMessage);
}

/** A password field error in the user's language; the server's text only for an unknown code or a missing parameter. */
export function passwordErrorText(error: PasswordFieldError, locale: Locale | undefined): string {
  if (!isPasswordErrorCode(error.code)) return error.message;
  const template = PASSWORD_ERRORS[lang(locale)][error.code];
  if (!template.includes("{n}")) return template;
  const n = parameterOf(error);
  return n === undefined ? error.message : template.replace("{n}", String(n));
}
